import { SubscriptionTypeNames } from '@eppendorf/vnls-subscription-utils';
import type { GetSubscriptionResponse } from '@eppendorf/vnls-user-tenant-utils';
import { keepPreviousData, QueryClient, useQuery } from '@tanstack/react-query';

import { fetcher } from '$shared/fetch';
import { SUBSCRIPTION_BASE_PATH } from '$shared/utils/api-paths';

const subscriptionQueryKey = 'subscription';

export const createGetSubscriptionQuery = () => ({
  queryKey: [subscriptionQueryKey, 'subscription-info'],
  queryFn: () =>
    fetcher.get<GetSubscriptionResponse>(`${SUBSCRIPTION_BASE_PATH}/subscription`),
  initialPageParam: undefined,
  staleTime: Infinity,
  placeholderData: keepPreviousData,
});

export const useGetSubscription = () => useQuery(createGetSubscriptionQuery());

export const getSubscriptionSync = async (queryClient: QueryClient) => {
  const query = createGetSubscriptionQuery();
  await queryClient.prefetchQuery(query);
  const cache = queryClient
    .getQueryCache()
    .find<GetSubscriptionResponse>({ queryKey: query.queryKey });
  return cache?.state.data;
};

export function isEssentialsPlan(
  subscription: GetSubscriptionResponse | undefined,
): boolean {
  return subscription?.subscription === SubscriptionTypeNames.Essentials;
}

export function isSampleSafetyPlusPlan(
  subscription: GetSubscriptionResponse | undefined,
): boolean {
  return subscription?.subscription === SubscriptionTypeNames.SampleSafetyPlus;
}

export function isFeatureLimited(
  subscription: GetSubscriptionResponse | undefined,
): boolean {
  return isEssentialsPlan(subscription);
}

export function getMaxAllowedDays(
  subscription: GetSubscriptionResponse | undefined,
): number {
  return isEssentialsPlan(subscription) ? 7 : 90;
}
